/**
 *
 * Functions exposed in this module are responsible for exposing the principal
 * logic in listening all properties for objects in the API
 *
 * Properties in objects always return their current values.
 *
 * You can listen for property changes using the .watch() method.
 *
 *  Watching property changes are handled with the .watch(property, callback)
 * method. The callback is called each time the watched property changes.
 *
 *  It is also possible to watch for a nested property, such as
 * `map.layer[xxxx].visibility`
 *
 *
 * @module geo/decorators
 * @preferred
 * @private
 */
import objectPath from 'object-path';
import watch from 'redux-watch';
import {Subscriber} from 'rxjs';
import {AsyncSubject} from 'rxjs/AsyncSubject';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {PartialObserver} from 'rxjs/Observer';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import {Subject} from 'rxjs/Subject';

/**
 * This decorator is used for implementing the watcher interface
 * @private
 * @param watcherSpec
 * @returns {(target: Function) => any}
 * @constructor
 */
export function Watch(watcherSpec: any = {}) {
  return (target: Function): any => {
    
    if (!target.prototype.on) {
      target.prototype.watch = function<T>(
          objectPathstr: string, observer: PartialObserver<T>,
          type: string = 'BehaviorSubject'): Subscriber<any> {
        return this.observable(objectPathstr, type).subscribe(observer);
      }
    }
    if (!target.prototype.on) {
      target.prototype.on = function<T>(
          objectPathstr: string, observer: PartialObserver<T>): Subscriber<T> {
        if (this.listenersNeeded !== undefined &&
            this.listenersNeeded[objectPathstr] !== undefined) {
          this.activateEvents([this.listenersNeeded[objectPathstr]]);
        }
        return this.observable(objectPathstr, 'Subject').subscribe(observer);
      }
    }
    if (!target.prototype.observable) {
      target.prototype.observable = function<T>(
          objectPathstr: string, type: string = 'BehaviorSubject'): Subject<T> {
            if (this.observers==undefined){
              this.observers={}
            }
        if (this.observers[objectPathstr] === undefined) {
          this.observers[objectPathstr] = [];
        }
        if (this.observers[objectPathstr][type] === undefined) {
          let w, initialValue, subject: Subject<T>;
          const store = this.store;
          const state = this.store.getState();
          if (watcherSpec[objectPathstr] === undefined) {
            let path;
            if (this.path !== '') {
              path = this.path + '.' + objectPathstr;
            } else {
              path = objectPathstr;
            }
            w = watch(store.getState, path);
            initialValue = objectPath.get(state, path);
          } else {
            const func = watcherSpec[objectPathstr];
            w = watch(function() {
              return func(store.getState());
            });
            initialValue = func(state);
          }
          switch (type) {
            case 'Subject':
              subject = new Subject<T>();
              break;
            case 'AsyncSubject':
              subject = new AsyncSubject<T>();
              break;
            case 'ReplaySubject':
              subject = new ReplaySubject<T>();
              break;
            case 'BehaviorSubject':
            default:
              subject = new BehaviorSubject<T>(initialValue);
              break;
          }
          this.observers[objectPathstr][type] = subject;
          store.subscribe(w((newVal: any, oldVal: any) => {
            subject.next(newVal);
          }));
          return subject;
        } else {
          return this.observers[objectPathstr][type];
        }
      }
    }
  }
}
